import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BatchOperationTypes } from 'dashboard/enums/batch-operation-types.enum';
import { batchOperationsListInterface } from 'dashboard/interfaces/batchOperationsListInterface';
import { DashboardWidgetConfigurationInterface } from 'dashboard/interfaces/dashboard-widget-configuration.interface';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { EMPTY, catchError, map } from 'rxjs';
import { FileUploadComponent } from 'shared/components/upload-files/file-upload.component';
import { BATCH_OPERATIONS_SETTINGS } from 'shared/constants/general-constants';
import { NotificationStatusEnum } from 'shared/constants/notification-statuses';
import { EventBusService, ToastService } from 'shared/services';
import { NotificationsService } from 'shared/services/notifications.service';
import { SharedModule } from 'shared/shared.module';

@Component({
    selector: 'app-batch-operations-dashboard-widget',
    imports: [TranslateModule, FileUploadComponent, ReactiveFormsModule, SharedModule],
    templateUrl: './batch-operations-dashboard-widget.component.html',
    styleUrl: './batch-operations-dashboard-widget.component.scss',
})
export class BatchOperationsDashboardWidgetComponent implements OnInit {
    @Input() dashboardWidgetConfiguration?: DashboardWidgetConfigurationInterface;

    private multipleTypesOfBatchOperationsExist: boolean = false;
    protected batchFile!: File | null;
    protected readonly batchOperationsSettings = BATCH_OPERATIONS_SETTINGS;
    protected fileForm: UntypedFormGroup = new UntypedFormGroup({
        file: new UntypedFormControl(null),
    });
    protected batchOperations: batchOperationsListInterface[] = this.multipleTypesOfBatchOperationsExist
        ? []
        : [
              {
                  isInProgress: false,
                  batchOperationType: BatchOperationTypes.LISTINGS_CREATION,
                  translationPath: 'dashboard.batchOperations.type.listingsCreation',
                  operationName: 'Listings Creation',
              },
          ];

    constructor(
        private dashboardService: DashboardService,
        private translate: TranslateService,
        private toastService: ToastService,
        private notificationsService: NotificationsService,
        private destroyRef: DestroyRef,
        private eventBusService: EventBusService,
    ) {}

    ngOnInit(): void {
        if (this.multipleTypesOfBatchOperationsExist) {
            // Call BE endpoint to retrieve the types, similar to Reports widget
            // and move the 'this.setIsInProgressFlag()' method call inside the subscribe,
            // so that it's called after receiving all the batchOperation types
        }

        this.setIsInProgressFlag();

        this.eventBusService
            .getObservableForEventStream('notification')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setIsInProgressFlag());
    }

    public uploadBatchOperation(event: File[], batchOperationType: BatchOperationTypes): void {
        const currentOperation = this.batchOperations.find((operation) => operation.batchOperationType === batchOperationType)!;
        this.batchFile = event[0] ?? null;

        if (!this.batchFile) {
            return;
        }

        currentOperation.isInProgress = true;

        this.dashboardService
            .batchOperationUpload(this.batchFile, currentOperation.batchOperationType)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    currentOperation.isInProgress = false;
                    this.fileForm.reset();
                    this.batchFile = null;

                    if (error.status === 502) {
                        this.toastService.showToast('dashboard.somethingWentWrong', 'error');
                        return EMPTY;
                    }

                    throw error;
                }),
            )
            .subscribe(() => {
                this.fileForm.reset();
                this.batchFile = null;
                this.toastService.showToast('dashboard.batchOperations.uploadSuccessful', 'success', {
                    batchOperationType: this.translate.instant(currentOperation.translationPath),
                });
            });
    }

    private setIsInProgressFlag() {
        this.batchOperations.forEach((batchOperation) => {
            this.notificationsService
                .getOperationStatus(batchOperation.operationName, 1, 1)
                .pipe(
                    takeUntilDestroyed(this.destroyRef),
                    map((response: any) => response.data?.[0]?.status),
                )
                .subscribe((status: any) => {
                    batchOperation.isInProgress = status == NotificationStatusEnum.IN_PROGRESS;
                });
        });
    }
}
